import { Download, FileIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../../../core/dialog';
import { formatFileSize } from './exercise-submission-helpers';

export interface PreviewableFile {
  url: string;
  filename?: string;
  contentType?: string;
  fileSize?: number;
}

interface FilePreviewDialogProps {
  file: PreviewableFile | null;
  onClose: () => void;
  downloadLabel?: string;
  unavailableMessage?: string;
}

function isOfficeDocument(contentType: string) {
  return (
    contentType.includes('presentation') ||
    contentType.includes('powerpoint') ||
    contentType.includes('msword') ||
    contentType.includes('wordprocessingml') ||
    contentType.includes('ms-excel') ||
    contentType.includes('spreadsheetml')
  );
}

const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  mp4: 'video/mp4',
  webm: 'video/webm',
  mov: 'video/quicktime',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  m4a: 'audio/x-m4a',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  ppt: 'application/vnd.ms-powerpoint',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

function inferContentType(file: PreviewableFile): string {
  if (file.contentType) {
    return file.contentType;
  }
  const extension = file.url.split('?')[0]?.split('.').pop()?.toLowerCase() ?? '';
  return EXTENSION_CONTENT_TYPES[extension] ?? '';
}

function renderPreview(file: PreviewableFile, unavailableMessage: string) {
  const contentType = inferContentType(file);

  if (contentType === 'application/pdf') {
    return (
      <iframe
        src={file.url}
        title={file.filename ?? 'PDF preview'}
        className="h-full w-full rounded-md border border-gray-200 bg-white"
      />
    );
  }

  if (contentType.startsWith('video/')) {
    return (
      <video
        src={file.url}
        controls
        autoPlay={false}
        className="h-full w-full rounded-md bg-black object-contain"
      />
    );
  }

  if (contentType.startsWith('audio/')) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <audio src={file.url} controls className="w-full max-w-xl" />
      </div>
    );
  }

  if (contentType.startsWith('image/')) {
    return (
      <img
        src={file.url}
        alt={file.filename ?? 'Image preview'}
        className="h-full w-full rounded-md object-contain"
      />
    );
  }

  if (isOfficeDocument(contentType)) {
    // Office Online can render publicly accessible Office files (PPT, Word, Excel) inline
    return (
      <iframe
        src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.url)}`}
        title={file.filename ?? 'Document preview'}
        className="h-full w-full rounded-md border border-gray-200 bg-white"
      />
    );
  }

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3 text-center">
      <FileIcon className="h-12 w-12 text-gray-300" />
      <p className="text-sm text-gray-500">{unavailableMessage}</p>
    </div>
  );
}

/**
 * Inline viewer for submitted files (PDF, video, audio, image, PowerPoint) so
 * reviewers can look at a submission without downloading it. The download
 * option stays available in the header.
 */
export function FilePreviewDialog({
  file,
  onClose,
  downloadLabel = 'Download',
  unavailableMessage = 'No inline preview available for this file type.',
}: FilePreviewDialogProps) {
  return (
    <Dialog open={!!file} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="flex h-[85vh] max-w-4xl flex-col gap-3 p-4 sm:p-6">
        {file && (
          <>
            <DialogHeader className="shrink-0 space-y-1 pr-8 text-left">
              <DialogTitle className="truncate text-base" title={file.filename}>
                {file.filename ?? 'File preview'}
              </DialogTitle>
              <DialogDescription className="flex items-center gap-3 text-xs">
                {typeof file.fileSize === 'number' && <span>{formatFileSize(file.fileSize)}</span>}
                <a
                  href={file.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium text-gray-700 underline underline-offset-2 hover:text-gray-900"
                >
                  <Download className="h-3.5 w-3.5" />
                  {downloadLabel}
                </a>
              </DialogDescription>
            </DialogHeader>
            <div className="min-h-0 flex-1">
              {renderPreview(file, unavailableMessage)}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
